<template>
  <div class="">
    <div
      class="w-full min-w-full bg-gradient-to-b from-gray-700 to-gray-900 overflow-hidden"
      style="height: 950px; max-height: 950px"
    >
      <transition name="fade">
        <div
          id="particles-js"
          class="w-full min-w-full z-10 relative"
          style="height: 950px"
        >
          <transition name="fade">
            <div
              v-if="comparisonResults !== null && comparisonResults !== undefined"
              class="w-full min-w-full h-full min-h-full absolute top-0 left-0 flex items-center justify-center"
            >
              <div class="flex items-center z-20 w-full max-w-4xl">
                <div class="w-full min-w-full">
                  <div class="text-white text-lg mb-2 -mt-4">
                    Results from 20,000 simulations
                  </div>

                  <div
                    v-if="flexiblePackageCost > 0"
                    class="flex justify-between items-center bg-white shadow-lg overflow-hidden mb-4 text-gray-800 border text-sm"
                  >
                    <div class="p-3 pl-4 pr-4 flex items-center leading-none">
                      <i
                        class="fas fa-info-circle text-gray-800 mr-2"
                        style="margin-top: 1px"
                      />
                      <span v-if="!existingFlexibleMembership">
                        <span class="mr-2 font-semibold">Recommended flexible package cost</span>
                        We have set your flexible package price at £{{ flexiblePackageCost }}
                        based on your full member price.
                      </span>
                      <span
                        v-if="existingFlexibleMembership"
                        class="ml-1"
                      >
                        <div class="mr-2 font-semibold mb-1">
                          Recommended flexible package price
                        </div>
                        We have set your flexible package price at £{{ flexiblePackageCost }}
                        based on the available cohorts for our platform.
                      </span>
                    </div>

                    <div class="mr-1" />
                  </div>

                  <div class="flex justify-between w-full min-w-full mb-2">
                    <div class="w-7/12">
                      <TabsNav
                        :tabs="tabs"
                        :selected-tab="selectedTab"
                        @select="selectTab"
                      />
                    </div>

                    <div class="flex items-center">
                      <div
                        class="flex ml-2 border transition-all duration-500 select-none shadow-lg font-semibold text-gray-700 bg-white"
                      >
                        <div
                          class="border-r p-1 pl-3 pr-3 flex items-center justify-center transition-all duration-200 cursor-pointer"
                          :class="
                            localYears > 2 ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400'
                          "
                          @click="decreaseYears()"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                            class="w-4 h-4 fill-current"
                          >
                            <path
                              d="M400 288h-352c-17.69
                              0-32-14.32-32-32.01s14.31-31.99
                               32-31.99h352c17.69 0 32 14.3 32
                               31.99S417.7 288 400 288z"
                            />
                          </svg>
                        </div>
                        <div class="p-1 pl-3 pr-3">
                          {{ localYears }} Years
                        </div>
                        <div
                          class="border-l p-1 pl-3 pr-3 flex items-center justify-center transition-all duration-200 cursor-pointer"
                          :class="
                            localYears < 3 ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400'
                          "
                          @click="increaseYears()"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                            class="w-4 h-4 fill-current"
                          >
                            <path
                              d="M432 256c0 17.69-14.33 32.01-32 32.01H256v144c0
                              17.69-14.33 31.99-32 31.99s-32-14.3-32-31.99v-144H48c-17.67
                              0-32-14.32-32-32.01s14.33-31.99 32-31.99H192v-144c0-17.69
                              14.33-32.01 32-32.01s32 14.32 32 32.01v144h144C417.7
                              224 432 238.3 432 256z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="flex w-full min-w-full mb-2">
                    <div class="w-7/12" />

                    <div class="w-5/12">
                      <div class="p-4 text-center shadow bg-secondary-100 font-medium text-white">
                        <div>
                          <div
                            class="leading-none text-2xl font-bold flex items-center justify-center"
                          >
                            <span v-if="localYears === 2">
                              {{
                                formatCurrency(
                                  comparisonResults.Product2[0].ProjectedProfit.Mean +
                                    comparisonResults.Product2[1].ProjectedProfit.Mean
                                )
                              }}
                            </span>
                            <span v-if="localYears === 3">
                              {{
                                formatCurrency(
                                  comparisonResults.Product2[0].ProjectedProfit.Mean +
                                    comparisonResults.Product2[1].ProjectedProfit.Mean +
                                    comparisonResults.Product2[2].ProjectedProfit.Mean
                                )
                              }}
                            </span>
                          </div>
                          <div class="text-sm font-normal mt-1">
                            average profit over {{ years }} years with PlayMoreGolf
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="relative flex items-center mb-8">
                    <div class="w-8/12 flex border bg-white shadow-lg p-8 pl-0">
                      <MetricLabels
                        class="border-r w-64 mr-2 pr-14 text-right font-semibold text-gray-800"
                        :selected-tab="selectedTab"
                        :year-index="yearIndex"
                        :existing-flexible-membership="existingFlexibleMembership"
                        :open-avg-total-cost-breakdown.sync="openAvgTotalCostBreakdown"
                        :open-payroll-breakdown.sync="openPayrollBreakdown"
                        :open-avg-total-revenue-breakdown.sync="openAvgTotalRevenueBreakdown"
                        :open-new-member-growth-breakdown.sync="openNewMemberGrowthBreakdown"
                        :open-member-retention-breakdown.sync="openMemberRetentionBreakdown"
                        :open-total-home-club-members-breakdown.sync="
                          openTotalHomeClubMembersBreakdown
                        "
                        :show-risk-of-loss="
                          comparisonResults.Product2[0].ProjectedProfit.RiskOfLoss > 0
                        "
                      />

                      <YearColumn
                        title="Year 1"
                        :comparison-results="comparisonResults"
                        :product-index="0"
                        :selected-tab="selectedTab"
                        :open-avg-total-cost-breakdown="openAvgTotalCostBreakdown"
                        :open-payroll-breakdown="openPayrollBreakdown"
                        :open-avg-total-revenue-breakdown="openAvgTotalRevenueBreakdown"
                        :open-new-member-growth-breakdown="openNewMemberGrowthBreakdown"
                        :open-member-retention-breakdown="openMemberRetentionBreakdown"
                        :open-total-home-club-members-breakdown="openTotalHomeClubMembersBreakdown"
                        :padding-left-class="localYears === 3 ? 'pl-4' : 'pl-20'"
                        :years="years"
                      />

                      <YearColumn
                        v-if="localYears === 3"
                        title="Year 2"
                        :comparison-results="comparisonResults"
                        :product-index="1"
                        :selected-tab="selectedTab"
                        :open-avg-total-cost-breakdown="openAvgTotalCostBreakdown"
                        :open-payroll-breakdown="openPayrollBreakdown"
                        :open-avg-total-revenue-breakdown="openAvgTotalRevenueBreakdown"
                        :open-new-member-growth-breakdown="openNewMemberGrowthBreakdown"
                        :open-member-retention-breakdown="openMemberRetentionBreakdown"
                        :open-total-home-club-members-breakdown="openTotalHomeClubMembersBreakdown"
                        padding-left-class="pl-4"
                        :years="years"
                      />
                    </div>

                    <!-- Space filler -->
                    <div class="w-4/12 select-none opacity-0">
                      <div class="p-8 pb-0">
                        <div
                          class="mb-5 mt-1 flex items-center justify-center"
                          style="padding-bottom: 7px"
                        >
                          <div>
                            <div class="flex">
                              <div class="w-7 h-7" />
                              <div class="w-7 h-7 bg-pmg-400" />
                              <div class="w-7 h-7" />
                            </div>
                            <div class="flex">
                              <div class="w-7 h-7 bg-pmg-400" />
                              <div class="w-7 h-7 bg-pmg-800" />
                              <div class="w-7 h-7 bg-pmg-100" />
                            </div>
                            <div class="flex">
                              <div class="w-7 h-7" />
                              <div class="w-7 h-7 bg-pmg-100" />
                              <div class="w-7 h-7" />
                            </div>
                          </div>
                        </div>

                        <div
                          class="flex items-center justify-center text-lg font-bold heading text-gray-800 mb-2"
                        >
                          Year 2
                        </div>

                        <div class="py-2">
                          <div
                            v-if="selectedTab === 'Summary'"
                            class="flex items-center justify-center"
                          >
                            <div class="font-semibold">
                              {{ formatCurrency(getTotalCosts(1)) }}
                            </div>
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{ formatCurrency(getPartnershipFee(1)) }}
                          </div>
                          <div
                            v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
                            style="margin-top: 2px"
                          >
                            {{ formatCurrency(0) }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{ formatCurrency(comparisonResults.Product2[1].AvgProjectedCardFees) }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[1].AvgProjectedCustServCost +
                                  comparisonResults.Product2[1].AvgProjectedRenewalCost +
                                  comparisonResults.Product2[1].AvgProjectedSalesOnboardingCost +
                                  comparisonResults.Product2[1].MarketingCost +
                                  comparisonResults.Product2[1].SocialCost
                              )
                            }}
                          </div>

                          <div
                            v-if="
                              selectedTab === 'Summary' &&
                                openAvgTotalCostBreakdown &&
                                openPayrollBreakdown
                            "
                            style="margin-top: 2px"
                          >
                            {{
                              formatCurrency(comparisonResults.Product2[1].AvgProjectedCustServCost)
                            }}
                          </div>
                          <div
                            v-if="
                              selectedTab === 'Summary' &&
                                openAvgTotalCostBreakdown &&
                                openPayrollBreakdown
                            "
                          >
                            {{
                              formatCurrency(comparisonResults.Product2[1].AvgProjectedRenewalCost)
                            }}
                          </div>
                          <div
                            v-if="
                              selectedTab === 'Summary' &&
                                openAvgTotalCostBreakdown &&
                                openPayrollBreakdown
                            "
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[1].AvgProjectedSalesOnboardingCost
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{ formatCurrency(comparisonResults.Product2[1].MarketingCost) }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{ formatCurrency(comparisonResults.Product2[1].SocialCost) }}
                          </div>

                          <div v-if="selectedTab === 'Costs'">
                            {{
                              formatCurrency(comparisonResults.Product2[1].ProjectedFixedCosts.Max)
                            }}
                          </div>
                          <div v-if="selectedTab === 'Profit'">
                            {{ formatCurrency(comparisonResults.Product2[1].ProjectedProfit.Max) }}
                          </div>
                          <div v-if="selectedTab === 'ROI'">
                            {{
                              formatPercentage(comparisonResults.Product2[1].ProjectedROI.Max * 100)
                            }}
                          </div>

                          <div v-if="selectedTab === 'Revenue'">
                            {{ formatCurrency(comparisonResults.Product2[1].NewMemberRevenue) }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openNewMemberGrowthBreakdown">
                            {{
                              comparisonResults.Product2[1].NewMembers
                                ? comparisonResults.Product2[0].NewMembers
                                : 0
                            }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openNewMemberGrowthBreakdown">
                            {{ formatCurrency(comparisonResults.Product2[1].NewMemberRevenue) }}
                          </div>
                        </div>

                        <div class="py-2">
                          <div
                            v-if="selectedTab === 'Summary'"
                            class="flex items-center justify-center"
                          >
                            <div class="font-semibold">
                              {{
                                formatCurrency(
                                  comparisonResults.Product2[1].AvgProjectedNYTotalRevenue
                                )
                              }}
                            </div>
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                            {{
                              formatCurrency(comparisonResults.Product2[1].AvgProjectedNYRetention)
                            }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                            {{ formatCurrency(comparisonResults.Product2[1].AvgProjectedNYGrowth) }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[1].AvgProjectedNYAwayRevenue
                              )
                            }}
                          </div>

                          <div
                            v-if="selectedTab === 'Costs'"
                            class="text-xl"
                          >
                            {{
                              formatCurrency(comparisonResults.Product2[1].ProjectedFixedCosts.Mean)
                            }}
                          </div>
                          <div
                            v-if="selectedTab === 'Profit'"
                            class="text-xl"
                          >
                            {{ formatCurrency(comparisonResults.Product2[1].ProjectedProfit.Mean) }}
                          </div>
                          <div
                            v-if="selectedTab === 'ROI'"
                            class="text-xl"
                          >
                            {{
                              formatPercentage(
                                comparisonResults.Product2[1].ProjectedROI.Mean * 100
                              )
                            }}
                          </div>

                          <div
                            v-if="selectedTab === 'Revenue'"
                            class="flex items-center justify-center"
                          >
                            {{
                              formatCurrency(comparisonResults.Product2[1].RetainedMemberRevenue)
                            }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openMemberRetentionBreakdown">
                            {{ comparisonResults.Product2[1].RetainedMembers }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openMemberRetentionBreakdown">
                            {{
                              formatCurrency(comparisonResults.Product2[1].RetainedMemberRevenue)
                            }}
                          </div>
                        </div>

                        <div class="py-2">
                          <div
                            v-if="selectedTab === 'Summary'"
                            class="font-semibold"
                          >
                            {{ formatCurrency(comparisonResults.Product2[1].ProjectedProfit.Mean) }}
                          </div>
                          <div v-if="selectedTab === 'Costs'">
                            {{
                              formatCurrency(comparisonResults.Product2[1].ProjectedFixedCosts.Min)
                            }}
                          </div>
                          <div v-if="selectedTab === 'Profit'">
                            {{ formatCurrency(comparisonResults.Product2[1].ProjectedProfit.Min) }}
                          </div>
                          <div v-if="selectedTab === 'ROI'">
                            {{
                              formatPercentage(comparisonResults.Product2[1].ProjectedROI.Min * 100)
                            }}
                          </div>

                          <div v-if="selectedTab === 'Revenue'">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[1].NewMemberRevenue +
                                  comparisonResults.Product2[1].RetainedMemberRevenue
                              )
                            }}
                          </div>
                          <div
                            v-if="selectedTab === 'Revenue' && openTotalHomeClubMembersBreakdown"
                          >
                            {{
                              comparisonResults.Product2[1].NewMembers +
                                comparisonResults.Product2[1].RetainedMembers
                            }}
                          </div>
                          <div
                            v-if="selectedTab === 'Revenue' && openTotalHomeClubMembersBreakdown"
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[1].NewMemberRevenue +
                                  comparisonResults.Product2[1].RetainedMemberRevenue
                              )
                            }}
                          </div>
                        </div>

                        <div
                          v-if="
                            selectedTab === 'Profit' &&
                              comparisonResults.Product2[0].ProjectedProfit.RiskOfLoss > 0
                          "
                          class="py-2"
                        >
                          <div
                            v-if="comparisonResults.Product2[1].ProjectedProfit.RiskOfLoss !== 0.0"
                          >
                            {{
                              formatPercentage(
                                comparisonResults.Product2[1].ProjectedProfit.RiskOfLoss
                              )
                            }}
                          </div>
                          <div v-else>
                            0%
                          </div>
                        </div>
                      </div>

                      <div class="p-4">
                        <a
                          href="https://www.playmore.golf/club-partner/"
                          target="_blank"
                        >
                          <div
                            class="uppercase text-sm font-medium leading-none cursor-pointer transition-all duration-500 hover:bg-secondary-200 bg-secondary-100 select-none text-white p-4"
                          >
                            <i class="fas fa-flag mr-2" />
                            Become a club partner
                          </div>
                        </a>
                      </div>
                    </div>

                    <div
                      class="absolute top-0 right-0 bg-white shadow-xl border text-center text-gray-800 w-5/12"
                    >
                      <div class="p-8 pb-0">
                        <div
                          class="mb-5 mt-1 flex items-center justify-center"
                          style="padding-bottom: 7px"
                        >
                          <div>
                            <div class="flex">
                              <div class="w-7 h-7" />
                              <div class="w-7 h-7 bg-pmg-400" />
                              <div class="w-7 h-7" />
                            </div>
                            <div class="flex">
                              <div class="w-7 h-7 bg-pmg-400" />
                              <div class="w-7 h-7 bg-pmg-800" />
                              <div class="w-7 h-7 bg-pmg-100" />
                            </div>
                            <div class="flex">
                              <div class="w-7 h-7" />
                              <div class="w-7 h-7 bg-pmg-100" />
                              <div class="w-7 h-7" />
                            </div>
                          </div>
                        </div>

                        <div
                          class="flex items-center justify-center text-lg font-bold heading text-gray-800 mb-2"
                        >
                          Year {{ years }}
                        </div>

                        <div class="py-2">
                          <div
                            v-if="selectedTab === 'Summary'"
                            class="flex items-center justify-center"
                          >
                            <div class="font-semibold">
                              {{ formatCurrency(getTotalCosts(localYears - 1)) }}
                            </div>
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{ formatCurrency(getPartnershipFee(localYears - 1)) }}
                          </div>
                          <div
                            v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
                            style="margin-top: 2px"
                          >
                            {{ formatCurrency(0) }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].AvgProjectedCardFees
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1]
                                  .AvgProjectedCustServCost +
                                  comparisonResults.Product2[localYears - 1]
                                    .AvgProjectedRenewalCost +
                                  comparisonResults.Product2[localYears - 1]
                                    .AvgProjectedSalesOnboardingCost +
                                  comparisonResults.Product2[localYears - 1].MarketingCost +
                                  comparisonResults.Product2[localYears - 1].SocialCost
                              )
                            }}
                          </div>

                          <div
                            v-if="
                              selectedTab === 'Summary' &&
                                openAvgTotalCostBreakdown &&
                                openPayrollBreakdown
                            "
                            style="margin-top: 2px"
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].AvgProjectedCustServCost
                              )
                            }}
                          </div>
                          <div
                            v-if="
                              selectedTab === 'Summary' &&
                                openAvgTotalCostBreakdown &&
                                openPayrollBreakdown
                            "
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].AvgProjectedRenewalCost
                              )
                            }}
                          </div>
                          <div
                            v-if="
                              selectedTab === 'Summary' &&
                                openAvgTotalCostBreakdown &&
                                openPayrollBreakdown
                            "
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1]
                                  .AvgProjectedSalesOnboardingCost
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].MarketingCost
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                            {{
                              formatCurrency(comparisonResults.Product2[localYears - 1].SocialCost)
                            }}
                          </div>

                          <div v-if="selectedTab === 'Profit'">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].ProjectedProfit.Max
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'ROI'">
                            {{
                              formatPercentage(
                                comparisonResults.Product2[localYears - 1].ProjectedROI.Max * 100
                              )
                            }}
                          </div>

                          <div v-if="selectedTab === 'Revenue'">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].NewMemberRevenue
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openNewMemberGrowthBreakdown">
                            {{
                              comparisonResults.Product2[localYears - 1].NewMembers
                                ? comparisonResults.Product2[localYears - 1].NewMembers
                                : 0
                            }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openNewMemberGrowthBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].NewMemberRevenue
                              )
                            }}
                          </div>
                        </div>

                        <div class="py-2">
                          <div
                            v-if="selectedTab === 'Summary'"
                            class="flex items-center justify-center"
                          >
                            <div class="font-semibold">
                              {{
                                formatCurrency(
                                  comparisonResults.Product2[localYears - 1]
                                    .AvgProjectedNYTotalRevenue
                                )
                              }}
                            </div>
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].AvgProjectedNYRetention
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].AvgProjectedNYGrowth
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].AvgProjectedNYAwayRevenue
                              )
                            }}
                          </div>

                          <div
                            v-if="selectedTab === 'Profit'"
                            class="text-xl"
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].ProjectedProfit.Mean
                              )
                            }}
                          </div>
                          <div
                            v-if="selectedTab === 'ROI'"
                            class="text-xl"
                          >
                            {{
                              formatPercentage(
                                comparisonResults.Product2[localYears - 1].ProjectedROI.Mean * 100
                              )
                            }}
                          </div>

                          <div v-if="selectedTab === 'Revenue'">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].RetainedMemberRevenue
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openMemberRetentionBreakdown">
                            {{ comparisonResults.Product2[localYears - 1].RetainedMembers }}
                          </div>
                          <div v-if="selectedTab === 'Revenue' && openMemberRetentionBreakdown">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].RetainedMemberRevenue
                              )
                            }}
                          </div>
                        </div>

                        <div class="py-2">
                          <div
                            v-if="selectedTab === 'Summary'"
                            class="font-semibold"
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].ProjectedProfit.Mean
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'Costs'">
                            {{ formatCurrency(getPartnershipFee(localYears - 1)) }}
                          </div>
                          <div v-if="selectedTab === 'Profit'">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].ProjectedProfit.Min
                              )
                            }}
                          </div>
                          <div v-if="selectedTab === 'ROI'">
                            {{
                              formatPercentage(
                                comparisonResults.Product2[localYears - 1].ProjectedROI.Min * 100
                              )
                            }}
                          </div>

                          <div v-if="selectedTab === 'Revenue'">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].NewMemberRevenue +
                                  comparisonResults.Product2[localYears - 1].RetainedMemberRevenue
                              )
                            }}
                          </div>
                          <div
                            v-if="selectedTab === 'Revenue' && openTotalHomeClubMembersBreakdown"
                          >
                            {{
                              comparisonResults.Product2[localYears - 1].NewMembers +
                                comparisonResults.Product2[localYears - 1].RetainedMembers
                            }}
                          </div>
                          <div
                            v-if="selectedTab === 'Revenue' && openTotalHomeClubMembersBreakdown"
                          >
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].NewMemberRevenue +
                                  comparisonResults.Product2[localYears - 1].RetainedMemberRevenue
                              )
                            }}
                          </div>
                        </div>

                        <div
                          v-if="selectedTab === 'Revenue'"
                          class="py-2 font-normal"
                        >
                          <div class="flex items-center justify-center">
                            {{
                              formatCurrency(
                                comparisonResults.Product2[localYears - 1].AvgProjectedNYAwayRevenue
                              )
                            }}
                          </div>
                        </div>

                        <div
                          v-if="
                            selectedTab === 'Profit' &&
                              comparisonResults.Product2[0].ProjectedProfit.RiskOfLoss > 0
                          "
                          class="py-2"
                        >
                          <div
                            v-if="
                              comparisonResults.Product2[localYears - 1].ProjectedProfit
                                .RiskOfLoss !== 0.0
                            "
                          >
                            {{
                              formatPercentage(
                                comparisonResults.Product2[localYears - 1].ProjectedProfit
                                  .RiskOfLoss
                              )
                            }}
                          </div>
                          <div v-else>
                            0%
                          </div>
                        </div>
                      </div>

                      <div class="p-4">
                        <a
                          href="https://www.playmore.golf/club-partner/"
                          target="_blank"
                        >
                          <div
                            class="uppercase text-sm font-medium leading-none cursor-pointer transition-all duration-500 hover:bg-secondary-200 bg-secondary-100 select-none text-white p-4"
                          >
                            <i class="fas fa-flag mr-2" />
                            Become a club partner
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
  import ParticlesJS from 'particles.js';
  import YearColumn from './yearColumn.vue';
  import TabsNav from './tabsNav.vue';
  import MetricLabels from './metricLabels.vue';

  export default {
    components: {
      YearColumn,
      TabsNav,
      MetricLabels,
    },

    props: {
      comparisonResults: {
        type: Object,
        default: null,
      },
      yearIndex: {
        type: Number,
        default: 0,
      },
      years: {
        type: Number,
        default: 1,
      },
      flexiblePackageCost: {
        type: Number,
        default: 0,
      },
      link: {
        type: String,
        default: '',
      },
      selectedTabProp: {
        type: String,
        default: 'Summary',
      },
    },

    data() {
      return {
        localYears: this.years,
        selectedTab: 'Summary',
        tabs: ['Summary', 'Revenue', 'Costs', 'Profit', 'ROI'],

        openAvgTotalCostBreakdown: false,
        openPayrollBreakdown: false,

        openAvgTotalRevenueBreakdown: false,

        openNewMemberGrowthBreakdown: false,
        openMemberRetentionBreakdown: false,
        openTotalHomeClubMembersBreakdown: false,
      };
    },

    computed: {
      existingFlexibleMembership() {
        return this.$route.params.existingFlexibleMembership === 'true';
      },
    },

    watch: {
      years: {
        handler(newVal) {
          this.localYears = newVal;
        },
        immediate: true,
      },
    },

    mounted() {
      this.$nextTick(() => {
        this.initParticleJS();
      });
      this.selectedTab = this.selectedTabProp;
    },

    methods: {
      getPartnershipFee(yearIndex) {
        if (this.localYears === 2) {
          return 1750;
        }
        if (this.localYears === 3) {
          if (yearIndex === 0) return 600; // Year 1
          if (yearIndex === 1) return 1575; // Year 2
          if (yearIndex === 2) return 1575; // Year 3
        }
        return (
          (this.comparisonResults.Product2
            && this.comparisonResults.Product2[yearIndex]
            && this.comparisonResults.Product2[yearIndex].Fee)
          || 0
        );
      },

      getTotalCosts(yearIndex) {
        if (!this.comparisonResults.Product2 || !this.comparisonResults.Product2[yearIndex])
          return 0;

        const product = this.comparisonResults.Product2[yearIndex];
        return (
          this.getPartnershipFee(yearIndex)
          + (product.AvgProjectedCardFees || 0)
          + (product.AvgProjectedCustServCost || 0)
          + (product.AvgProjectedRenewalCost || 0)
          + (product.AvgProjectedSalesOnboardingCost || 0)
          + (product.MarketingCost || 0)
          + (product.SocialCost || 0)
        );
      },

      increaseYears() {
        if (this.localYears < 3) {
          this.localYears = this.localYears + 1;
          this.$emit('incrementYears');
        }
      },

      decreaseYears() {
        if (this.localYears > 2) {
          this.localYears = this.localYears - 1;
          this.$emit('decrementYears');
        }
      },

      formatCurrency(value) {
        return value
          .toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
          .split('.')[0];
      },

      formatPercentage(value) {
        return `${value.toFixed(2)}%`;
      },

      nextYear() {
        this.$emit('incrementYear');
      },

      previousYear() {
        this.$emit('decrementYear');
      },

      selectTab(tab) {
        if (tab !== this.selectedTab) {
          this.$emit('selectTab', tab);
          this.selectedTab = tab;
        }
      },

      initParticleJS() {
        particlesJS('particles-js', {
          particles: {
            number: {
              value: 150,
              density: {
                enable: true,
                value_area: 800,
              },
            },
            color: {
              value: '#FFF',
            },
            shape: {
              type: 'circle',
              stroke: {
                width: 0,
                color: '#000000',
              },
              polygon: {
                nb_sides: 3,
              },
            },
            opacity: {
              value: 0.5,
              random: false,
              anim: {
                enable: false,
                speed: 1,
                opacity_min: 0.1,
                sync: false,
              },
            },
            size: {
              value: 3,
              random: true,
              anim: {
                enable: false,
                speed: 40,
                size_min: 0.1,
                sync: false,
              },
            },
            line_linked: {
              enable: true,
              distance: 150,
              color: '#FFF',
              opacity: 0.4,
              width: 1,
            },
            move: {
              enable: true,
              speed: 1,
              direction: 'none',
              random: false,
              straight: false,
              out_mode: 'out',
              bounce: false,
              attract: {
                enable: false,
                rotateX: 600,
                rotateY: 1200,
              },
            },
          },
          interactivity: {
            detect_on: 'canvas',
            events: {
              onhover: {
                enable: false,
                mode: 'grab',
              },
              onclick: {
                enable: true,
                mode: 'push',
              },
              resize: false,
            },
            modes: {
              grab: {
                distance: 140,
                line_linked: {
                  opacity: 1,
                },
              },
              bubble: {
                distance: 400,
                size: 40,
                duration: 2,
                opacity: 8,
                speed: 3,
              },
              repulse: {
                distance: 200,
                duration: 0.4,
              },
              push: {
                particles_nb: 4,
              },
              remove: {
                particles_nb: 2,
              },
            },
          },
          retina_detect: true,
        });
      },
    },
  };
</script>
